import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, X, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../components/ui/cn.js';
import { listPortalAdmins, addPortalAdmin, removePortalAdmin } from '../api/seller.js';
import type { AddPortalAdminBodyV2 } from '../api/seller.js';
import { extractErrorMessage } from '../lib/errors.js';

function fmt(iso: string) {
  return new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(new Date(iso));
}

function initials(name: string) {
  return name.split(' ').map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

export function AdminsPage() {
  const qc = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [newName, setNewName]   = useState('');
  const [newMobile, setNewMobile] = useState('');
  const [removingId, setRemovingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['seller-portal-admins'],
    queryFn:  listPortalAdmins,
  });

  const addMutation = useMutation({
    mutationFn: (body: AddPortalAdminBodyV2) => addPortalAdmin(body),
    onSuccess: () => {
      toast.success('Admin added successfully.');
      qc.invalidateQueries({ queryKey: ['seller-portal-admins'] });
      setShowAddModal(false);
      setNewName(''); setNewMobile('');
    },
    onError: (err) => toast.error(extractErrorMessage(err, 'Failed to add admin.')),
  });

  const removeMutation = useMutation({
    mutationFn: (id: string) => removePortalAdmin(id),
    onSuccess: () => {
      toast.success('Admin removed.');
      qc.invalidateQueries({ queryKey: ['seller-portal-admins'] });
      setRemovingId(null);
    },
    onError: (err) => {
      toast.error(extractErrorMessage(err, 'Failed to remove admin.'));
      setRemovingId(null);
    },
  });

  const admins = data?.admins ?? [];

  function handleAdd() {
    if (!newName.trim() || !newMobile.trim()) {
      toast.error('Name and mobile are required.'); return;
    }
    addMutation.mutate({ name: newName.trim(), mobile: newMobile.trim() });
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-slate-900">Admins</h1>
          <p className="text-sm text-slate-500 mt-0.5">Manage admin accounts that have access to this portal.</p>
        </div>
        <button type="button" onClick={() => setShowAddModal(true)}
          className="flex items-center gap-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 px-3 py-2 rounded-lg transition-all shadow-sm shrink-0">
          <Plus className="w-3.5 h-3.5" />
          Add Admin
        </button>
      </div>

      {/* Admin list table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/80 border-b border-slate-100">
            <tr>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-10">#</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Admin</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Mobile</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Role</th>
              <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">Since</th>
              <th className="px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {isLoading
              ? [0, 1].map((i) => (
                <tr key={i} className="border-b border-slate-50">
                  {[0, 1, 2, 3, 4, 5].map((j) => (
                    <td key={j} className="px-5 py-3.5"><div className="h-4 bg-slate-100 rounded animate-pulse w-20" /></td>
                  ))}
                </tr>
              ))
              : admins.map((admin, i) => (
              <tr key={admin.id} className={cn('transition-colors', i < admins.length - 1 && 'border-b border-slate-50')}>
                <td className="px-5 py-3.5 text-xs text-slate-400 tabular-nums">{i + 1}</td>

                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-50 border border-blue-200 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-blue-600">{initials(admin.name)}</span>
                    </div>
                    <p className="text-sm font-semibold text-slate-900">{admin.name}</p>
                  </div>
                </td>

                <td className="px-5 py-3.5 text-sm text-slate-600">{admin.mobile}</td>

                <td className="px-5 py-3.5">
                  <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-md ring-1 bg-blue-50 text-blue-700 ring-blue-200/60">
                    <ShieldCheck className="w-3 h-3" />
                    Admin
                  </span>
                </td>

                <td className="px-5 py-3.5 text-xs text-slate-400 tabular-nums">{fmt(admin.addedAt)}</td>

                <td className="px-5 py-3.5 text-right">
                  <button type="button"
                    disabled={removingId === admin.id}
                    onClick={() => { setRemovingId(admin.id); removeMutation.mutate(admin.id); }}
                    className="inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50 transition-colors">
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                </td>
              </tr>
            ))}
            {!isLoading && admins.length === 0 && (
              <tr>
                <td colSpan={6} className="px-5 py-10 text-center text-sm text-slate-400">No admins added yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add Admin Modal */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-bold text-slate-900">Add Admin</h2>
              <button type="button" onClick={() => setShowAddModal(false)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors">
                <X className="w-4 h-4 text-slate-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label htmlFor="admin-name" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Full Name *</label>
                <input id="admin-name" type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
                  placeholder="Admin full name" className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" />
              </div>
              <div>
                <label htmlFor="admin-mobile" className="block text-xs font-semibold text-slate-700 mb-1.5 uppercase tracking-wide">Mobile *</label>
                <input id="admin-mobile" type="tel" value={newMobile} onChange={(e) => setNewMobile(e.target.value)}
                  placeholder="+91 98765 43210" className="w-full px-3 py-2.5 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all" />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button type="button" onClick={() => setShowAddModal(false)}
                className="flex-1 py-2.5 rounded-lg border border-slate-200 text-sm font-semibold text-slate-600 hover:bg-slate-50 transition-all">
                Cancel
              </button>
              <button type="button" onClick={handleAdd} disabled={addMutation.isPending}
                className="flex-1 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 transition-all inline-flex items-center justify-center gap-2">
                {addMutation.isPending && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {addMutation.isPending ? 'Adding…' : 'Add Admin'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
